// Keep in sync with the default --side-nav-width and $mini-width in SideNav.module.scss.

/** Open width when none is saved, and after a double-click on the edge. */
export const DEFAULT_OPEN_WIDTH = 220;
/** Narrowest open width; dragging past it toward mini rubber-bands, then snaps. */
export const MIN_OPEN_WIDTH = 180;
/** Widest open width, however long a label: enough for nearly every client and entity name. */
export const MAX_OPEN_WIDTH = 400;
export const MINI_WIDTH = 56;

/** An open width between the minimum and `max`. */
export const clampOpenWidth = ({
    width,
    max,
}: Record<"width" | "max", number>) =>
    Math.min(Math.max(width, MIN_OPEN_WIDTH), max);

/**
 * How far a label's text runs past its box. Measured in fractional pixels:
 * `scrollWidth` and `clientWidth` are rounded, so a label a fraction of a
 * pixel short reads as fitting there, and its ellipsis stays.
 */
const textOverflowOf = (label: Element) => {
    const text = document.createRange();
    text.selectNodeContents(label);
    return (
        text.getBoundingClientRect().width - label.getBoundingClientRect().width
    );
};

/**
 * The widest open width worth having for a nav whose open width is `width`:
 * where every rendered label shows in full, and never short of `width` or the
 * default, so a narrowed nav can always widen back. At most `MAX_OPEN_WIDTH`.
 * Labels in collapsed sections aren't laid out, so they don't count.
 */
export const fittedOpenWidth = ({
    nav,
    width,
}: {
    nav: HTMLElement;
    width: number;
}) => {
    // Measured no wider than drawn, as it can still be animating open, and no
    // wider than `width`: springing back from a stretch, every label fits.
    const measuredWidth = Math.min(width, nav.getBoundingClientRect().width);
    const overflows = [...nav.querySelectorAll("[data-side-nav-label]")].map(
        textOverflowOf,
    );
    return clampOpenWidth({
        width: Math.max(
            DEFAULT_OPEN_WIDTH,
            width,
            Math.ceil(measuredWidth + Math.max(0, ...overflows)),
        ),
        max: MAX_OPEN_WIDTH,
    });
};
